import { useState } from 'react'
import { motion } from 'framer-motion'
import { Layout } from '@/components/Layout'
import { DepartmentCard } from '@/components/Cards'
import { departments } from '@/lib/index'
import { fadeInUp, staggerContainer, staggerItem, springPresets } from '@/lib/motion'
import { IMAGES } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { GraduationCap, Users, BookOpen, Briefcase, CheckCircle2 } from 'lucide-react'

export default function Departments() {
  const [selectedDept, setSelectedDept] = useState(departments[0])

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.STUDENTS_LAB_1}
              alt="مختبر الطلاب"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-l from-primary via-accent to-primary bg-clip-text text-transparent">
                الأقسام الأكاديمية
              </h1>
              <p className="text-xl text-muted-foreground">
                استكشف تخصصاتنا الأكاديمية المتنوعة واختر المسار المهني المناسب لك
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-8">
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="lg:col-span-4 space-y-4"
              >
                <h2 className="text-2xl font-bold mb-6">اختر التخصص</h2>
                {departments.map((dept) => (
                  <motion.div key={dept.id} variants={staggerItem}>
                    <Button
                      variant={selectedDept.id === dept.id ? 'default' : 'outline'}
                      className="w-full justify-start h-auto py-4 px-6 text-right"
                      onClick={() => setSelectedDept(dept)}
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="flex-1">
                          <div className="font-bold text-lg mb-1">{dept.nameAr}</div>
                          <div className="text-sm opacity-80">{dept.nameEn}</div>
                        </div>
                        <Badge variant="secondary" className="shrink-0">
                          {dept.studentCount}+ طالب
                        </Badge>
                      </div>
                    </Button>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                key={selectedDept.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={springPresets.gentle}
                className="lg:col-span-8"
              >
                <Card className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-l from-primary/10 via-accent/5 to-primary/10">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-3xl mb-2">{selectedDept.nameAr}</CardTitle>
                        <CardDescription className="text-lg">{selectedDept.nameEn}</CardDescription>
                      </div>
                      <Badge variant="default" className="text-lg px-4 py-2">
                        <Users className="w-5 h-5 ml-2" />
                        {selectedDept.studentCount}+ طالب
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="p-6">
                    <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                      {selectedDept.description}
                    </p>

                    <div className="mb-8">
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <GraduationCap className="w-6 h-6 text-primary" />
                        المستويات الدراسية المتاحة
                      </h3>
                      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                        {selectedDept.degreeLevels.map((level, idx) => {
                          const colors = ['bg-blue-600', 'bg-indigo-600', 'bg-amber-600', 'bg-emerald-600'];
                          return (
                            <Card key={idx} className="overflow-hidden">
                              <div className={`${colors[idx] || 'bg-primary'} p-3 text-white text-center`}>
                                <div className="font-bold text-base">
                                  {level.split('(')[0].trim()}
                                </div>
                              </div>
                              <CardContent className="p-3 text-center">
                                <div className="text-sm text-muted-foreground">
                                  {level.match(/\((.+)\)/)?.[1]}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>

                    <Separator className="my-8" />

                    <Tabs defaultValue="courses" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="courses">المقررات الدراسية</TabsTrigger>
                        <TabsTrigger value="careers">الفرص الوظيفية</TabsTrigger>
                        <TabsTrigger value="requirements">شروط القبول</TabsTrigger>
                      </TabsList>

                      <TabsContent value="courses" className="mt-6">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <BookOpen className="w-6 h-6 text-primary" />
                            المقررات الدراسية الرئيسية
                          </h3>
                          <div className="grid md:grid-cols-2 gap-3">
                            {selectedDept.courses.map((course, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05, ...springPresets.snappy }}
                              >
                                <Card className="bg-card/50 hover:bg-card transition-colors">
                                  <CardContent className="p-4 flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                                    <span className="font-medium">{course}</span>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="careers" className="mt-6">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <Briefcase className="w-6 h-6 text-primary" />
                            الفرص الوظيفية المتاحة
                          </h3>
                          <div className="grid md:grid-cols-2 gap-3">
                            {selectedDept.careers.map((career, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05, ...springPresets.snappy }}
                              >
                                <Card className="bg-gradient-to-bl from-accent/10 to-primary/5 hover:from-accent/20 hover:to-primary/10 transition-all">
                                  <CardContent className="p-4 flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-accent shrink-0" />
                                    <span className="font-medium">{career}</span>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="requirements" className="mt-6">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <CheckCircle2 className="w-6 h-6 text-primary" />
                            شروط القبول والتسجيل
                          </h3>
                          <div className="space-y-3">
                            {selectedDept.requirements.map((req, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05, ...springPresets.snappy }}
                              >
                                <Card className="bg-muted/30">
                                  <CardContent className="p-4 flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                      <span className="text-primary font-bold">{idx + 1}</span>
                                    </div>
                                    <span className="text-lg">{req}</span>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>

                    <div className="mt-8 p-6 bg-gradient-to-l from-primary/10 via-accent/5 to-primary/10 rounded-lg">
                      <div className="text-center">
                        <h3 className="text-xl font-bold mb-2">هل أنت مهتم بهذا التخصص؟</h3>
                        <p className="text-muted-foreground mb-4">
                          ابدأ رحلتك الأكاديمية معنا اليوم
                        </p>
                        <Button size="lg" className="gap-2">
                          <GraduationCap className="w-5 h-5" />
                          التسجيل الآن
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4">جميع التخصصات</h2>
              <p className="text-xl text-muted-foreground">
                استعرض كافة البرامج الأكاديمية المتاحة
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {departments.map((dept) => (
                <motion.div key={dept.id} variants={staggerItem}>
                  <DepartmentCard department={dept} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  )
}
